/**
 * Step 2: File Upload
 *
 * Upload CV file(s) with drag-and-drop.
 * Supports single file or bulk (up to 50 files) based on import method.
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import FileDropzone from "../FileDropzone";
import type { WizardState } from "./CandidateImportWizard";

const MAX_BULK_FILES = 50;

const CV_FORMATS = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
};

interface Props {
  wizardState: WizardState;
  onNext: () => void;
  onBack: () => void;
  updateState: (updates: Partial<WizardState>) => void;
}

const Step2_Upload: React.FC<Props> = ({
  wizardState,
  onNext,
  onBack,
  updateState,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const isBulk = wizardState.importMethod === "bulk-cv";

  // Single mode state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Bulk mode state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleSingleFileSelect = (file: File) => {
    setSelectedFile(file);
    updateState({ uploadedFile: file });
  };

  const handleBulkFilesSelect = (files: File[]) => {
    setSelectedFiles((prev) => {
      const combined = [...prev, ...files];
      const capped = combined.slice(0, MAX_BULK_FILES);
      return capped;
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
  };

  const handleNext = () => {
    if (isBulk) {
      if (selectedFiles.length > 0) {
        updateState({ uploadedFiles: selectedFiles });
        onNext();
      }
    } else {
      if (selectedFile) {
        onNext();
      }
    }
  };

  const canProceed = isBulk ? selectedFiles.length > 0 : !!selectedFile;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  if (isBulk) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {t("importWizard.step2.bulkTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t("importWizard.step2.bulkSubtitle", { max: MAX_BULK_FILES })}
        </Typography>

        <FileDropzone
          onFileSelect={() => {}}
          onFilesSelect={handleBulkFilesSelect}
          acceptedFormats={CV_FORMATS}
          maxSizeMB={10}
          multiple
        />

        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Chip
                label={t("importWizard.step2.filesSelected", {
                  count: selectedFiles.length,
                  max: MAX_BULK_FILES,
                })}
                color={
                  selectedFiles.length >= MAX_BULK_FILES ? "warning" : "primary"
                }
                variant="outlined"
              />
              <Button size="small" color="error" onClick={handleClearAll}>
                {t("common:actions.clearAll")}
              </Button>
            </Box>
            <List
              dense
              sx={{
                maxHeight: 300,
                overflow: "auto",
                bgcolor: "background.default",
                borderRadius: 1,
              }}
            >
              {selectedFiles.map((file, index) => (
                <ListItem key={`${file.name}-${index}`} divider>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button onClick={onBack} variant="outlined">
            {t("common:actions.back")}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed}
            data-testid="next-button"
          >
            {t("importWizard.step2.nextParseBulk", {
              count: selectedFiles.length,
            })}
          </Button>
        </Box>
      </Box>
    );
  }

  // Single CV mode (existing behavior)
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("importWizard.step2.singleTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("importWizard.step2.singleSubtitle")}
      </Typography>

      <FileDropzone
        onFileSelect={handleSingleFileSelect}
        acceptedFormats={CV_FORMATS}
        maxSizeMB={10}
      />

      {selectedFile && (
        <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
          {t("importWizard.step2.selected", {
            name: selectedFile.name,
            size: formatFileSize(selectedFile.size),
          })}
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button onClick={onBack} variant="outlined">
          {t("common:actions.back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!selectedFile}
          data-testid="next-button"
        >
          {t("importWizard.step2.nextParseSingle")}
        </Button>
      </Box>
    </Box>
  );
};

export default Step2_Upload;
